import React, { useState, useEffect, useContext, useRef } from 'react';
import SocketContext from '../../../../../../../NewWebSocket/components/WebSocketAdapter';
import './HealthControl.css';

interface HealthControlProps {
  championId: string;
  currentHealth: number;
  maxHealth: number;
}

const HealthControl: React.FC<HealthControlProps> = ({
  championId,
  currentHealth,
  maxHealth
}) => {
  const socket = useContext(SocketContext);
  const healthBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Calculate health percentage
  const healthPercentage = Math.round((currentHealth / maxHealth) * 100);
  
  // Control states
  const [sliderPercentage, setSliderPercentage] = useState(healthPercentage);
  const [healthValue, setHealthValue] = useState(currentHealth.toString());
  
  // Update control states when health changes
  useEffect(() => {
    setSliderPercentage(healthPercentage);
    setHealthValue(currentHealth.toString());
  }, [currentHealth, maxHealth, healthPercentage]);
  
  // Handler for setting health by absolute value
  const handleHealthValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHealthValue(value);
    if (!isNaN(parseInt(value, 10))) {
      const numericValue = parseInt(value, 10);
      setSliderPercentage(Math.min(Math.round((numericValue / maxHealth) * 100), 100));
    }
  };
  
  // Handler for setting health by percentage
  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percentage = parseInt(e.target.value, 10);
    setSliderPercentage(percentage);
    setHealthValue(Math.round(maxHealth * percentage / 100).toString());
  };
  
  // Handler for applying the health change
  const applyHealthChange = () => {
    if (!socket) return;
    
    console.log(`[HealthControl] Setting champion ${championId} health to ${healthValue}`);
    socket.emit('set-champion-health', {
      championId: championId,
      value: parseInt(healthValue, 10) || 0,
      isPercentage: false
    });
  };

  // Spinner button handlers
  const incrementHealth = () => {
    const newValue = Math.min(parseInt(healthValue, 10) + 1, maxHealth);
    setHealthValue(newValue.toString());
    setSliderPercentage(Math.round((newValue / maxHealth) * 100));
  };

  const decrementHealth = () => {
    const newValue = Math.max(parseInt(healthValue, 10) - 1, 0);
    setHealthValue(newValue.toString());
    setSliderPercentage(Math.round((newValue / maxHealth) * 100));
  };

  const incrementPercentage = () => {
    const newPercentage = Math.min(sliderPercentage + 1, 100);
    setSliderPercentage(newPercentage);
    setHealthValue(Math.round(maxHealth * newPercentage / 100).toString());
  };

  const decrementPercentage = () => {
    const newPercentage = Math.max(sliderPercentage - 1, 0);
    setSliderPercentage(newPercentage);
    setHealthValue(Math.round(maxHealth * newPercentage / 100).toString());
  };

  // Calculate health percentage from click/touch position
  const calculatePercentageFromPosition = (clientX: number) => {
    if (!healthBarRef.current) return;
    
    const rect = healthBarRef.current.getBoundingClientRect();
    const position = clientX - rect.left; 
    const totalWidth = rect.width;
    
    // Calculate percentage based on position
    let percentage = Math.round((position / totalWidth) * 100);
    
    // Clamp percentage between 0 and 100
    percentage = Math.max(0, Math.min(100, percentage));
    
    // Update slider percentage and health value
    setSliderPercentage(percentage);
    setHealthValue(Math.round(maxHealth * percentage / 100).toString());
  };

  // Event handlers for mouse/touch interaction with health bar
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    calculatePercentageFromPosition(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      calculatePercentageFromPosition(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    calculatePercentageFromPosition(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isDragging) {
      calculatePercentageFromPosition(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Setup global mouse/touch event listeners for dragging
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    const handleGlobalTouchEnd = () => {
      setIsDragging(false);
    };

    // Add global event listeners
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalTouchEnd);

    // Clean up
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, []);

  return (
    <div className="health-control">
      {/* Health display and controls */}
      <div className="health-control-container">
        <div className="health-input-row">
          <h4>Health</h4>
          <div className="health-inputs">
            <span>Current HP</span>
            <div className="health-input-container">
              <input
                type="number"
                min="0"
                max={maxHealth}
                value={healthValue}
                onChange={handleHealthValueChange}
                className="health-value-input"
              />
              <div className="custom-spinner">
                <button className="spinner-button" onClick={incrementHealth}></button>
                <button className="spinner-button spinner-down" onClick={decrementHealth}></button>
              </div>
            </div>
            <span>/{maxHealth}</span>
            <span>(</span>
            <div className="health-input-container">
              <input
                type="number"
                min="0"
                max="100"
                value={sliderPercentage}
                onChange={handlePercentageChange}
                className="health-percentage-input"
              />
              <div className="custom-spinner">
                <button className="spinner-button" onClick={incrementPercentage}></button>
                <button className="spinner-button spinner-down" onClick={decrementPercentage}></button>
              </div>
            </div>
            <span>%)</span>
          </div>
          <button 
            onClick={applyHealthChange}
            className="apply-button"
          >
            Apply
          </button>
        </div>
        
        {/* Interactive Health bar */}
        <div 
          className="health-bar"
          ref={healthBarRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Current health */}
          <div 
            className="health-fill"
            style={{ width: `${Math.min(healthPercentage, 100)}%` }}
          ></div>
          
          {/* Preview - Increasing health (adding green) */}
          {sliderPercentage > healthPercentage && (
            <div 
              className="health-preview-increase"
              style={{ 
                width: `${Math.min(sliderPercentage - healthPercentage, 100)}%`,
                left: `${healthPercentage}%`
              }}
            ></div>
          )}
          
          {/* Preview - Decreasing health (adding gray overlay) */}
          {sliderPercentage < healthPercentage && (
            <div 
              className="health-preview-decrease"
              style={{ 
                width: `${Math.min(healthPercentage - sliderPercentage, 100)}%`,
                left: `${sliderPercentage}%`
              }}
            ></div>
          )}
          
          {/* Slider indicator dot */}
          <div 
            className="health-slider-indicator"
            style={{ left: `calc(${sliderPercentage}% - 5px)` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default HealthControl; 